import type { RefObject } from "react";

/** Elements that take keyboard focus. */
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const AUTOFOCUS = "[data-autofocus]";
const BODY_FOCUSABLE = FOCUSABLE.split(",")
  .map((selector) => `.nb-modal-body ${selector.trim()}`)
  .join(", ");
const FOOTER_BUTTON = ".nb-modal-foot button:not([disabled])";

/**
 * Where focus lands when the dialog opens: the given element, else one marked `data-autofocus`,
 * else the first field of the body, else the first footer button, else the dialog itself.
 */
export function initialFocusTarget(dialog: HTMLElement, initialFocusRef?: RefObject<HTMLElement | null>): HTMLElement {
  return (
    initialFocusRef?.current ??
    dialog.querySelector<HTMLElement>(AUTOFOCUS) ??
    dialog.querySelector<HTMLElement>(BODY_FOCUSABLE) ??
    dialog.querySelector<HTMLElement>(FOOTER_BUTTON) ??
    dialog
  );
}

/** The focusable elements in tab order, leaving out hidden ones (except the focused one). */
export function tabbableIn(container: HTMLElement, focused: Element | null): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === focused);
}

/**
 * Keeps Tab inside the dialog: from the last element it wraps to the first and, with Shift, from
 * the first to the last. Returns the element to focus, or undefined to let the browser move on.
 */
export function wrapFocus(tabbable: HTMLElement[], focused: Element | null, backwards: boolean): HTMLElement | undefined {
  const first = tabbable[0];
  const last = tabbable[tabbable.length - 1];
  if (!first || !last) return undefined;
  if (backwards && focused === first) return last;
  if (!backwards && focused === last) return first;
  return undefined;
}
